/*
 * Mobile Stability §8 — iPhone voice diagnostics page.
 *
 * Route: /app/talk/diagnostics  (hidden from public, require login).
 *
 * Runs the six stage checks the P0 hotfix brief asks for:
 *   1. Microphone (permission + stream + track + AudioContext + input level)
 *   2. Recording  (5-second capture; local playback button)
 *   3. STT        (post the clip to /api/kalki/stt; show returned text)
 *   4. KALKI AI   (post the transcript as a chat question; show reply)
 *   5. ElevenLabs (fetch /api/kalki/tts; play on this device)
 *   6. Full call  (chain 1→5 and report which stage first fails)
 *
 * No API keys exposed; endpoints are the same auth-gated routes used in
 * production. No raw audio stored on the backend.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/hooks/useAuth";

const INK   = "#F8FAFC";
const MUTE  = "#A1A1AA";
const GREEN = "#22C55E";
const RED   = "#EF4444";
const AMBER = "#F59E0B";

const Row = ({ label, status, note }) => (
  <div style={{ display: "flex", justifyContent: "space-between",
                padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
    <div style={{ color: INK, fontSize: 13 }}>{label}</div>
    <div style={{ color: status === "pass" ? GREEN
                       : status === "fail" ? RED
                       : status === "warn" ? AMBER : MUTE, fontSize: 13 }}>
      {note || status.toUpperCase()}
    </div>
  </div>
);

export default function TalkDiagnostics() {
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const [recUrl, setRecUrl] = useState(null);
  const [recText, setRecText] = useState("");
  const [kalkiText, setKalkiText] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const rafRef = useRef(null);

  const set = (k, status, note) =>
    setResults((r) => ({ ...r, [k]: { status, note } }));

  useEffect(() => () => {
    try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch (_) {}
    try { ctxRef.current?.close(); } catch (_) {}
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const runAll = async () => {
    setRunning(true);
    setResults({});
    setRecUrl(null); setRecText(""); setKalkiText("");

    // ── 1. MICROPHONE ──────────────────────────────────────────────
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;
      const track = stream.getAudioTracks()[0];
      set("mic_perm", track?.readyState === "live" ? "pass" : "fail",
          `track=${track?.readyState || "none"} enabled=${track?.enabled}`);
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      ctxRef.current = ctx;
      if (ctx.state === "suspended") await ctx.resume();
      set("audio_ctx", ctx.state === "running" ? "pass" : "warn", `state=${ctx.state}`);
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteTimeDomainData(buf);
        let mn = 255, mx = 0;
        for (let i = 0; i < buf.length; i++) {
          if (buf[i] < mn) mn = buf[i];
          if (buf[i] > mx) mx = buf[i];
        }
        setMicLevel((mx - mn) / 255);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
      set("input_level", "warn", "watch the bar — should move when you speak");
    } catch (e) {
      set("mic_perm", "fail", e?.name || e?.message || String(e));
      setRunning(false); return;
    }

    // ── 2. RECORDING (5s) ──────────────────────────────────────────
    let blob;
    try {
      const candidates = ["audio/mp4", "audio/mp4;codecs=mp4a.40.2",
                           "audio/webm;codecs=opus", "audio/webm"];
      let mime = "";
      for (const c of candidates) {
        if (window.MediaRecorder?.isTypeSupported?.(c)) { mime = c; break; }
      }
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime })
                        : new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (ev) => ev.data?.size && chunks.push(ev.data);
      const done = new Promise((res) => rec.addEventListener("stop", res, { once: true }));
      rec.start();
      await new Promise((r) => setTimeout(r, 5000));
      try { rec.stop(); } catch (_) {}
      await done;
      blob = new Blob(chunks, { type: rec.mimeType || mime || "audio/mp4" });
      setRecUrl(URL.createObjectURL(blob));
      set("recording", blob.size > 2048 ? "pass" : "fail",
          `${blob.size}B · ${rec.mimeType || mime}`);
    } catch (e) {
      set("recording", "fail", e?.message || String(e));
      setRunning(false); return;
    }

    // ── 3. STT ──────────────────────────────────────────────────────
    let transcript = "";
    try {
      const ext = (blob.type.includes("mp4") || blob.type.includes("aac")) ? "m4a" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `diag.${ext}`);
      const r = await api.post("/kalki/stt", fd);
      transcript = (r.data?.text || "").trim();
      setRecText(transcript);
      set("stt", transcript ? "pass" : "warn",
          transcript ? `${transcript.length} chars` : "empty transcript");
    } catch (e) {
      set("stt", "fail", `${e?.response?.status || ""} ${e?.message}`);
    }

    // ── 4. KALKI AI ────────────────────────────────────────────────
    try {
      const q = transcript || "KALKI, say hello.";
      const r = await api.post("/kalki/respond", { text: q, mode: "chat" });
      const reply = r.data?.reply || "";
      setKalkiText(reply);
      set("kalki_ai", reply ? "pass" : "fail", `${reply.length} chars`);
    } catch (e) {
      set("kalki_ai", "fail", `${e?.response?.status || ""} ${e?.message}`);
    }

    // ── 5. ElevenLabs TTS ──────────────────────────────────────────
    try {
      const r = await api.post("/kalki/tts",
        { text: "Namaste, friend. This is a short diagnostic test." },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = new Audio(url);
      el.playsInline = true;
      await el.play();
      set("tts", "pass", `${r.data.size}B · playing`);
    } catch (e) {
      set("tts", "fail", `${e?.response?.status || ""} ${e?.message}`);
    }

    set("full_call", "pass", "finished");
    setRunning(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0F", color: INK, padding: 20 }}
         data-testid="talk-diagnostics">
      <div style={{ fontSize: 11, letterSpacing: "0.3em", color: MUTE }}>
        ✦ KALKI · TALK DIAGNOSTICS
      </div>
      <div style={{ fontSize: 18, margin: "6px 0 16px" }}>
        iPhone voice pipeline tester
      </div>

      <button
        onClick={runAll}
        disabled={running}
        data-testid="run-diagnostics"
        style={{
          background: "linear-gradient(135deg, #5D00FF, #FF2E9A)",
          color: "white", padding: "12px 22px", borderRadius: 999,
          letterSpacing: "0.2em", fontSize: 12, opacity: running ? 0.6 : 1,
        }}
      >
        {running ? "RUNNING…" : "RUN ALL 6 TESTS"}
      </button>

      <div style={{ marginTop: 20, background: "rgba(255,255,255,0.04)",
                    borderRadius: 14, overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.08)" }}>
        <Row label="1. Microphone permission + track"
             status={results.mic_perm?.status || "idle"} note={results.mic_perm?.note} />
        <Row label="   AudioContext"
             status={results.audio_ctx?.status || "idle"} note={results.audio_ctx?.note} />
        <Row label={`   Live input level`}
             status={results.input_level?.status || "idle"}
             note={`${(micLevel * 100).toFixed(0)}%`} />
        <Row label="2. Recording (5s)"
             status={results.recording?.status || "idle"} note={results.recording?.note} />
        <Row label="3. Speech-to-text (/kalki/stt)"
             status={results.stt?.status || "idle"} note={results.stt?.note} />
        <Row label="4. KALKI AI reply (/kalki/respond)"
             status={results.kalki_ai?.status || "idle"} note={results.kalki_ai?.note} />
        <Row label="5. ElevenLabs TTS (/kalki/tts)"
             status={results.tts?.status || "idle"} note={results.tts?.note} />
      </div>

      {recUrl && (
        <div style={{ marginTop: 16 }}>
          <div style={{ color: MUTE, fontSize: 11, marginBottom: 6 }}>
            Local playback of the 5s capture (proves iPhone recorded audio):
          </div>
          <audio controls src={recUrl} playsInline style={{ width: "100%" }} />
        </div>
      )}
      {recText && (
        <div style={{ marginTop: 14, color: INK, fontSize: 13 }}>
          <span style={{ color: MUTE, fontSize: 11 }}>Transcript:</span><br />
          {recText}
        </div>
      )}
      {kalkiText && (
        <div style={{ marginTop: 14, color: INK, fontSize: 13 }}>
          <span style={{ color: MUTE, fontSize: 11 }}>KALKI:</span><br />
          {kalkiText}
        </div>
      )}
      <div style={{ marginTop: 24, color: MUTE, fontSize: 11 }}>
        No audio or transcript is persisted on the backend by this page.
        Private route · logged-in users only.
      </div>
    </div>
  );
}
